import { createModel } from '@rematch/core';
import {
    HistoryType,
    PatientHistory
} from '../../models/patient-history';
import { getPatientHistories } from '../../apis/patient-history';

interface ProcessData {
    byType: Dictionary<number[]>;
    byId: Dictionary<PatientHistory>;
}

export interface State {
    byType: Dictionary<number[]>;
    byId: Dictionary<PatientHistory>;
    loading: Dictionary<boolean>;
}

export const patientHistories = createModel<State>({
    state: {
        byType: {},
        byId: {},
        loading: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<PatientHistory[]>,
            loadingKey?: string
        ): State => {
            const processedData = storeByType(data);
            return {
                ...state,
                byType: {
                    ...state.byType,
                    ...processedData.byType
                },
                byId: {
                    ...state.byId,
                    ...processedData.byId
                },
                ...loadingKey && {
                    loading: {
                        ...state.loading,
                        [loadingKey]: false
                    }
                }
            };
        },
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        })
    },
    effects: dispatch => ({
        async getPatientHistoriesAsync(type: Undefinable<HistoryType>) {
            const {
                icd10s,
                snomeds
            } = dispatch;
            const loadingKey = type === undefined ? 'getPatientHistoriesAsync' : `getPatientHistoriesAsync---${type}`;
            this.loading(loadingKey, true);
            try {
                const data = await getPatientHistories();
                this.update(data, loadingKey);

                // Collect related data
                if (data) {
                    snomeds.getSnomedsAsync({
                        conceptIds: data.filter(x => x.historyType === HistoryType.SocialHistory).map(x => x.code),
                        active: true
                    });
                    snomeds.getSnomedsAsync({
                        conceptIds: data.filter(x =>
                            x.historyType !== HistoryType.FamilyHistory &&
                            x.historyType !== HistoryType.PastMedicalHistory &&
                            x.historyType !== HistoryType.DrinkingHistory &&
                            x.historyType !== HistoryType.SmokingHistory
                        ).map(x => x.code),
                        active: false
                    });
                    icd10s.getICD10sAsync(data.filter(x =>
                        x.historyType === HistoryType.FamilyHistory ||
                        x.historyType === HistoryType.PastMedicalHistory
                    ).map(x => x.icd10Code));
                }
            } catch (e) {
                window.console.log(e);
                this.loading(loadingKey, true);
            }
        }
    })
});

export const storeByType = (arr: Undefinable<PatientHistory[]>): ProcessData => {
    const byType = {} as Dictionary<number[]>;
    const byId = {} as Dictionary<PatientHistory>;
    if (arr) {
        arr.sort((a, b) => a.createdDate > b.createdDate ? 1 : -1);
        for (let i = 0; i < arr.length; i++) {
            byId[arr[i].id] = arr[i];

            if (!byType[arr[i].historyType]) {
                byType[arr[i].historyType] = [];
            }
            byType[arr[i].historyType]!.push(arr[i].id);
        }
    }
    return {
        byType,
        byId
    };
};
